import { Ref } from 'vue'
import BrandMapping from '@/helper/BrandMapping'
import userStore from '@/store/UserStore'
import i18n, { formatPrice as translationFormatPrice } from '@/helper/Translations'
import ProjectBaseData from '@/services/models/ProjectBaseData'
import { Environment } from '@/helper/Types'

export function isDesktop(): boolean {
  return (window.innerWidth > 0 ? window.innerWidth : screen.width) >= 1200
}

export function copyToClipboard(data: string) {
  window.navigator.clipboard.writeText(data).then(() => {
    alert('Successfully copied data to clipboard')
    console.debug(data)
  }).catch(error => {
    alert(error.message)
  })
}

export function environmentDetector() {
  const env = window.location.hostname.toLowerCase()

  if (env === 'localhost') {
    return Environment.LOCALHOST
  } else if (env === 'systemconfigurator.vaillant-group.com' || env === 'sc.prod.ldust.azure.vaillant-group.com') {
    return Environment.PRODUCTION
  } else if (env === 'systemconfigurator-qa.vaillant-group.com' || env === 'sc.stage.ldust.azure.vaillant-group.com') {
    return Environment.STAGE
  } else if (env.includes('sc.dev.ldust.azure')) {
    return env.split('.')[0].toUpperCase()
  }
}

export function generateRandomId(): string {
  return Math.floor(Math.random() * 0xca0e373ebffff + 0x05c5e45240000).toString(36)
}

export function refValue<T>(val: Ref<T> | T): T {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return 'value' in (typeof val === 'object' ? val : {}) ? (val as Ref<T>).value : (val as T)
}

/**
 * Wait until execute a function. Use: `debounce(() => {}, 500)`
 * @param func execute method
 * @param wait time of milliseconds to wait
 */
export const debounce = function(func: () => void, wait = 300) {
  let timer: ReturnType<typeof setTimeout>

  return function(this: any, ...args: any) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, wait)
  }
}

export function buildURLQueryParams(values: Record<string, string>) {
  const params = new URLSearchParams()

  for (const key in values) {
    params.append(key, values[key])
  }

  return Array.from(params).length ? '?' + params : ''
}

/**
 * Filter all keys with empty string values from object recursively
 */
export function removeEmptyValuesFromObject(obj: Record<string, any>) {
  const newObj = {}
  Object.keys(obj).forEach(prop => {
    if (obj[prop] !== '') {
      if (typeof obj[prop] === 'object') {
        const temp = removeEmptyValuesFromObject(obj[prop])
        if (Object.keys(temp).length) {
          (newObj as any)[prop] = temp
        }
      } else {
        (newObj as any)[prop] = obj[prop]
      }
    }
  })
  return newObj
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function setMetaTitle(title: string) {
  document.title = title + (title !== '' ? ' - ' : '') + i18n.global.t('global.project_name')
}

export function setFavicon(brand: string) {
  const link = document.createElement('link')
  const appleLink = document.createElement('link')
  link.rel = 'icon'
  appleLink.rel = 'apple-touch-icon'
  link.href = '/static/img/favicon-' + BrandMapping.getFavicon(brand) + '.ico'
  appleLink.href = '/static/img/favicon-' + BrandMapping.getFavicon(brand) + '.ico'
  document.head.appendChild(link)
  document.head.appendChild(appleLink)
}

export function formatPrice(price: number | undefined): string {
  return translationFormatPrice(price, userStore.currentIsoLocale)
}

export function calculateDisplayPrice(price: number, quantity = 1): string {
  let vatRate = 0

  if (userStore.vatRate && userStore.enableDisplayOfVatPrices) {
    vatRate = userStore.vatRate
  }

  return formatPrice(price * quantity * (vatRate / 100 + 1))
}

export function writeJsonToClipboard(value: Record<string, any>) {
  const json = JSON.stringify(value, null, 2)
  copyToClipboard(json)
}

export function stripPersonalInformation(project: ProjectBaseData) {
  project.customerData = undefined
  project.projectAddress.street = undefined
  project.projectAddress.houseNumber = undefined
}
